import React, { useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, matchPath, useLocation } from 'react-router-dom';

type Crumb = { label: string; path?: string };

function buildCrumbs(pathname: string): Crumb[] {
  if (pathname === '/dashboard') {
    return [{ label: 'Dashboard' }];
  }

  const dash: Crumb = { label: 'Dashboard', path: '/dashboard' };

  if (pathname === '/stores') {
    return [dash, { label: 'My stores' }];
  }
  if (pathname === '/stores/create') {
    return [dash, { label: 'My stores', path: '/stores' }, { label: 'Create store' }];
  }
  if (matchPath({ path: '/stores/:id/edit', end: true }, pathname)) {
    return [dash, { label: 'My stores', path: '/stores' }, { label: 'Edit store' }];
  }
  if (matchPath({ path: '/stores/:id', end: true }, pathname)) {
    return [dash, { label: 'My stores', path: '/stores' }, { label: 'Store details' }];
  }

  if (pathname === '/listings') {
    return [dash, { label: 'My listings' }];
  }
  if (pathname === '/listings/create') {
    return [dash, { label: 'My listings', path: '/listings' }, { label: 'Create listing' }];
  }
  if (matchPath({ path: '/listings/:id/edit', end: true }, pathname)) {
    return [dash, { label: 'My listings', path: '/listings' }, { label: 'Edit listing' }];
  }

  if (pathname === '/leads') {
    return [dash, { label: 'Leads' }];
  }
  if (matchPath({ path: '/leads/:id', end: true }, pathname)) {
    return [dash, { label: 'Leads', path: '/leads' }, { label: 'Lead details' }];
  }

  if (pathname === '/reviews') {
    return [dash, { label: 'Reviews' }];
  }
  if (pathname === '/analytics') {
    return [dash, { label: 'Analytics' }];
  }
  if (pathname === '/notifications') {
    return [dash, { label: 'Notifications' }];
  }
  if (pathname === '/profile') {
    return [dash, { label: 'Profile' }];
  }
  if (pathname === '/settings') {
    return [dash, { label: 'Settings' }];
  }

  return [dash, { label: 'Page' }];
}

/**
 * Route-aware breadcrumbs for the vendor shell (dashboard home + nested sections).
 */
const VendorBreadcrumbs: React.FC = () => {
  const { pathname } = useLocation();

  const items = useMemo(() => {
    const crumbs = buildCrumbs(pathname);
    return crumbs.map((crumb, index) => {
      const isLast = index === crumbs.length - 1;
      const showLink = crumb.path && !isLast;

      let title: React.ReactNode = crumb.label;
      if (showLink) {
        title =
          index === 0 ? (
            <Link to={crumb.path!} className="min-w-0">
              <HomeOutlined className="shrink-0" aria-hidden />
              <span>{crumb.label}</span>
            </Link>
          ) : (
            <Link to={crumb.path!} className="min-w-0">
              {crumb.label}
            </Link>
          );
      } else if (index === 0 && crumbs.length === 1) {
        title = (
          <span className="inline-flex min-w-0 items-center">
            <HomeOutlined className="shrink-0" aria-hidden />
            <span>{crumb.label}</span>
          </span>
        );
      } else {
        title = <span>{crumb.label}</span>;
      }

      return { title };
    });
  }, [pathname]);

  return (
    <nav aria-label="Breadcrumb" className="mb-1 sm:mb-1.5">
      <Breadcrumb items={items} className="vendor-breadcrumb" />
    </nav>
  );
};

export default VendorBreadcrumbs;
